import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import { IconCalendar } from 'components/icons';

import { AppContext } from 'context/app';
import { EVENTS_HISTORY_RETENTION_DAYS } from 'context/events';
import { EventsFilterContext, EventsFilterDispatchContext } from 'context/eventsFilter';

import { adjustFilterRange } from 'modules/events';
import { getTimeRangeBasedOnRetention } from 'modules/time';

import styles from './dateFilter.module.scss';

export const DateFilter = () => {
  const { eventsFilterOpen } = useContext(AppContext);
  const { filterState } = useContext(EventsFilterContext);
  const dispatch = useContext(EventsFilterDispatchContext);

  const [{ startDate, endDate }, setState] = useState({
    startDate: filterState.startTime ? dayjs(filterState.startTime) : null,
    endDate: filterState.endTime ? dayjs(filterState.endTime) : null,
  });

  // State for date range constraints - recalculated when filter opens
  const [dateRangeConstraints, setDateRangeConstraints] = useState(() => {
    const { earliestStartTime, latestEndTime } = getTimeRangeBasedOnRetention(EVENTS_HISTORY_RETENTION_DAYS);
    // Use hour-precise boundaries to match backend validation
    return { earliestStartTime, latestEndTime };
  });

  // Track if we've already adjusted dates for this filter opening
  const hasAdjustedForCurrentOpen = useRef<boolean>(false);

  const minStartDate = dayjs(dateRangeConstraints.earliestStartTime);
  const maxStartDate =
    endDate && endDate.isBefore(dateRangeConstraints.latestEndTime)
      ? endDate
      : dayjs(dateRangeConstraints.latestEndTime);
  const minEndDate = startDate && startDate.isAfter(minStartDate) ? startDate : minStartDate;
  const maxEndDate = dayjs(dateRangeConstraints.latestEndTime);

  // Recalculate date range constraints when filter opens
  useEffect(() => {
    if (eventsFilterOpen) {
      const { earliestStartTime, latestEndTime } = getTimeRangeBasedOnRetention(EVENTS_HISTORY_RETENTION_DAYS);
      // Use hour-precise boundaries to match backend validation
      setDateRangeConstraints({ earliestStartTime, latestEndTime });

      // Only adjust once per filter opening to avoid infinite loops
      if (!hasAdjustedForCurrentOpen.current) {
        hasAdjustedForCurrentOpen.current = true;

        // Check if current filter values are still valid with the new range
        const currentStartTime = filterState.startTime;
        const currentEndTime = filterState.endTime;

        if (currentStartTime || currentEndTime) {
          const adjustedRange = adjustFilterRange({
            startTime: currentStartTime,
            endTime: currentEndTime,
            retentionDays: EVENTS_HISTORY_RETENTION_DAYS,
          });

          // If values were adjusted (clamped), update the filter state
          if (adjustedRange.startTime !== currentStartTime || adjustedRange.endTime !== currentEndTime) {
            dispatch({
              type: 'filterState',
              payload: {
                startTime: adjustedRange.startTime,
                endTime: adjustedRange.endTime,
              },
            });
          }
        }
      }
    } else {
      // Reset the flag when filter closes
      hasAdjustedForCurrentOpen.current = false;
    }
  }, [eventsFilterOpen]); // Only depend on eventsFilterOpen to avoid loops

  const handleStartDateChange = (date: Dayjs | null): void => {
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));

    dispatch({
      type: 'filterState',
      payload: { startTime: date?.startOf('day').valueOf() || null },
    });
  };

  const handleEndDateChange = (date: Dayjs | null): void => {
    setState((prevState) => ({
      ...prevState,
      endDate: date,
    }));

    dispatch({
      type: 'filterState',
      payload: { endTime: date?.endOf('day').valueOf() || null },
    });
  };

  useEffect(() => {
    setState({
      startDate: filterState.startTime ? dayjs(filterState.startTime) : null,
      endDate: filterState.endTime ? dayjs(filterState.endTime) : null,
    });
  }, [filterState]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className={styles.container}>
        <Stack direction="row" spacing={2}>
          <DatePicker
            className={styles.datePicker}
            label="Start"
            value={startDate}
            onChange={handleStartDateChange}
            minDate={minStartDate}
            maxDate={maxStartDate}
            disableFuture
            format="MMM DD YYYY"
            views={['day']}
            slots={{ openPickerIcon: IconCalendar }}
            slotProps={{
              popper: { className: styles.datePickerPopper, disablePortal: true },
            }}
          />
          <DatePicker
            className={styles.datePicker}
            label="End"
            value={endDate}
            onChange={handleEndDateChange}
            disableFuture
            minDate={minEndDate}
            maxDate={maxEndDate}
            format="MMM DD YYYY"
            views={['day']}
            slots={{ openPickerIcon: IconCalendar }}
            slotProps={{
              popper: { className: styles.datePickerPopper, disablePortal: true },
            }}
          />
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};
